import { SectionDef } from 'src/xgenerated/receptor'
import {
  ControlMappingItem,
  ControlMapping,
} from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { ControlMappingEntry } from './ConfigureReceptorForm.constants'

const parseConfigObject = (config: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(config)
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>
    }
  } catch {
    // noop - fall through to empty object
  }
  return {}
}

const getAllSources = (config: string): Record<string, unknown> => {
  const parsedConfig = parseConfigObject(config)
  const candidate = parsedConfig['all_sources']
  if (candidate && typeof candidate === 'object') {
    return candidate as Record<string, unknown>
  }
  return {}
}

const getSourcesByFieldKey = (
  sourceMap: Record<string, unknown>,
  fieldKey: string,
): Array<{
  id: string
  display: string
  parentid?: string
  childid?: unknown
  childids?: string
}> => {
  const fieldSources = sourceMap[fieldKey]
  if (!Array.isArray(fieldSources)) return []
  return fieldSources as Array<{
    id: string
    display: string
    parentid?: string
    childid?: unknown
    childids?: string
  }>
}

const parseChildIds = (raw?: unknown): string[] | undefined => {
  if (raw == null) return undefined

  if (Array.isArray(raw)) {
    const normalized = raw
      .map((v) => String(v).trim())
      .filter((v) => v.length > 0)
    return normalized.length > 0 ? normalized : undefined
  }

  if (typeof raw !== 'string') return undefined

  const trimmed = raw.trim()
  if (!trimmed) return undefined

  // Supports JSON array form if provided by backend (e.g. ["1","2"])
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v).trim()).filter((v) => v.length > 0)
      }
    } catch {
      // fallback to CSV parsing below
    }
  }

  // Default delimiter format used in existing config payloads.
  const parts = trimmed.split(',')
  const normalized = parts.map((v) => v.trim()).filter((v) => v.length > 0)
  return normalized.length > 0 ? normalized : undefined
}

export const createNewControlMapping = (
  fieldName: string,
  fieldKey: string,
): ControlMappingEntry => {
  let controlName: string
  let sourceName: string

  if (fieldKey === 'advance_mappings') {
    // ServiceNow uses an extened array to init records
    return {
      name: '',
      control: { name: 'Select Application', value: '' },
      source: { name: 'Select Table', value: '' },
      subjectField: { name: 'Select Subject Field', value: '' },
      enumField: { name: 'Select Enum Field', value: '' },
      enumChoice: { name: 'Select Enum Choice', value: '' },
    }
  } else if (fieldKey === 'project_mappings') {
    if (fieldName === 'Project') {
      // Jira
      controlName = 'Select Project'
      sourceName = 'Select Issue Type'
    } else {
      // ServiceNow
      controlName = 'Select Application'
      sourceName = 'Select Task Type'
    }
  } else if (fieldKey === 'urls') {
    controlName = 'Select Control'
    sourceName = fieldName
  } else {
    //control_mapping
    controlName = 'Select Control'
    sourceName = `Select ${fieldName}`
  }

  return {
    name: '',
    control: { name: controlName, value: '' },
    source: { name: sourceName, value: '' },
    isSplit: false,
  }
}

export const getSources = (config: string, fieldKey: string): SelectItem[] => {
  const sources = getAllSources(config)
  const fieldSources = getSourcesByFieldKey(sources, fieldKey)
  return fieldSources.map(
    (source: { id: string; display: string; parentid?: string }) => ({
      name: source.display,
      value: source.id,
      parentId: source.parentid,
    }),
  )
}

export const getControls = (config: string, fieldKey: string): SelectItem[] => {
  const controls = getAllSources(config)
  const fieldSources = getSourcesByFieldKey(controls, fieldKey)
  return fieldSources.map(
    (control: {
      id: string
      display: string
      childid?: unknown
      childids?: string
    }) => ({
      name: control.display,
      value: control.id,
      childIds:
        parseChildIds(control.childids) ?? parseChildIds(control.childid),
    }),
  )
}

export const createDBControlMappings = (
  sections: SectionDef[],
  mappings: Record<string, ControlMappingEntry[]>,
): ControlMapping[] => {
  const updatedMappings: ControlMapping[] = []
  sections.forEach((section) => {
    const relevantFields = section.fields.filter(
      (field) =>
        field.input_type === 'control_mapping' ||
        field.input_type === 'manual_control_mapping' ||
        field.input_type === 'stacked_mapping',
    )
    relevantFields.forEach((field) => {
      const fieldKey = field.field
      const caption = field.evidence_caption
      if (!mappings[fieldKey]) {
        return
      }
      // Add a workaround where in if initial value is unset, ignore
      if (
        mappings[fieldKey][0].controlError == 'Please select a value' ||
        mappings[fieldKey][0].sourceError == 'Please select a value' ||
        mappings[fieldKey][0].control.name == 'Select Control' ||
        mappings[fieldKey][0].source.name == 'Select Page Label'
      ) {
        return
      }

      mappings[fieldKey].forEach((mapping) => {
        const sourceName = (mapping.source.name as string).replaceAll('-', '--')
        const controlMsg = new ControlMappingItem()
          .setName(mapping.control.name as string)
          .setValue(mapping.control.value.toString())
        const evidenceMsg = new ControlMappingItem()
          .setName(`${caption} -- ${sourceName}`)
          .setValue(mapping.source.value.toString())
        const controlMapping = new ControlMapping()
          .setControl(controlMsg)
          .setSource(evidenceMsg)
        field.service_model_id &&
          controlMapping.setServiceModelId(field.service_model_id)
        updatedMappings.push(controlMapping)
      })
    })
  })
  return updatedMappings
}
